import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import FocusedDialog from '../ui/FocusedDialog';
import Button from '../ui/Button';
import { X, PauseCircle, PlayCircle, RefreshCw } from 'lucide-react';
import { MarketplaceListing } from '../../types/marketplace';
import marketplaceService from '../../services/marketplaceService';

interface PauseStrategiesDialogProps {
  listing: MarketplaceListing;
  /** `pause` flips every strategy to INACTIVE; `resume` flips them back to ACTIVE. */
  mode: 'pause' | 'resume';
  onClose: () => void;
  onSuccess: (result: { updated_count: number; total: number }) => void;
}

interface OpenPositionsCount {
  total: number;
  by_status: { open: number; entry_pending: number; exit_pending: number };
  affected_user_count: number;
}

/**
 * Custom confirmation dialog for the bulk pause/resume strategy action.
 * Mirrors UnpublishBasketDialog's structure so the admin sees consistent
 * "decide → preview impact → confirm" UX across wind-down steps.
 *
 * For pause: fetches live open-position count so the admin sees how many
 * positions will continue to exit naturally after entries stop. For resume:
 * the count is informational (resume re-enables entry firing).
 */
const PauseStrategiesDialog: React.FC<PauseStrategiesDialogProps> = ({
  listing, mode, onClose, onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openPositions, setOpenPositions] = useState<OpenPositionsCount | null>(null);
  const [positionsLoading, setPositionsLoading] = useState(true);
  const [positionsError, setPositionsError] = useState<string | null>(null);
  // Timestamp of the latest count fetch — shows the admin how stale the
  // displayed numbers are. F2 fix: counts can drift between dialog open and
  // confirm, especially during market hours; surface the timestamp + a
  // refresh button so the admin can re-check before clicking through.
  const [positionsFetchedAt, setPositionsFetchedAt] = useState<Date | null>(null);

  const isPause = mode === 'pause';
  const Icon = isPause ? PauseCircle : PlayCircle;
  const accent = isPause ? 'text-indigo-600 dark:text-indigo-400' : 'text-green-600 dark:text-green-400';
  const confirmBtn = isPause
    ? 'bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 dark:disabled:bg-indigo-800 text-white'
    : 'bg-green-600 hover:bg-green-700 disabled:bg-green-300 dark:disabled:bg-green-800 text-white';

  const fetchOpenPositions = useCallback(async () => {
    setPositionsLoading(true);
    setPositionsError(null);
    try {
      const data = await marketplaceService.getListingOpenPositions(listing.basket_id);
      setOpenPositions(data);
      setPositionsFetchedAt(new Date());
    } catch (e: any) {
      setPositionsError(e.message || 'Failed to load open positions');
    } finally {
      setPositionsLoading(false);
    }
  }, [listing.basket_id]);

  useEffect(() => {
    fetchOpenPositions();
  }, [fetchOpenPositions]);

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleConfirm = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      // F2: re-fetch counts right before mutating, so the success-toast
      // count is computed against fresh state — not whatever was loaded
      // when the dialog first opened.
      try {
        const fresh = await marketplaceService.getListingOpenPositions(listing.basket_id);
        setOpenPositions(fresh);
        setPositionsFetchedAt(new Date());
      } catch {
        // Best-effort — fall back to the existing count display if the
        // pre-confirm refresh fails. The action itself still proceeds.
      }
      const result = isPause
        ? await marketplaceService.pauseAllStrategiesInBasket(listing.basket_id)
        : await marketplaceService.resumeAllStrategiesInBasket(listing.basket_id);
      onSuccess({ updated_count: result.updated_count, total: result.total });
    } catch (err: any) {
      setError(err.message || `Failed to ${mode} strategies`);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <FocusedDialog
        ariaLabel={isPause ? 'Pause strategies' : 'Resume strategies'}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
      >
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Icon className={`h-5 w-5 ${accent}`} />
                {isPause ? 'Pause all strategies?' : 'Resume all strategies?'}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                disabled={isSubmitting}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">"{listing.title}"</p>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              {/* Live impact — F2: shows fetch timestamp + refresh icon so the
                  admin can re-check before confirming the action. The confirm
                  handler also auto-re-fetches as a safety net. */}
              <div className="bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300">
                {positionsLoading ? (
                  <span className="inline-flex items-center gap-1">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Checking live positions…
                  </span>
                ) : positionsError ? (
                  <span className="text-red-700 dark:text-red-300">
                    Couldn't load open positions: {positionsError}
                  </span>
                ) : openPositions ? (
                  <div className="space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-semibold">{listing.subscriber_count || 0}</span>{' '}
                        {(listing.subscriber_count || 0) === 1 ? 'subscriber' : 'subscribers'} ·{' '}
                        <span className="font-semibold">{openPositions.total}</span> open{' '}
                        {openPositions.total === 1 ? 'position' : 'positions'} across{' '}
                        <span className="font-semibold">{openPositions.affected_user_count}</span>{' '}
                        {openPositions.affected_user_count === 1 ? 'user' : 'users'}
                      </div>
                      <button
                        type="button"
                        onClick={fetchOpenPositions}
                        disabled={positionsLoading || isSubmitting}
                        title="Refresh count"
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {openPositions.total > 0 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {openPositions.by_status.open} open · {openPositions.by_status.entry_pending} entry-pending · {openPositions.by_status.exit_pending} exit-pending
                      </div>
                    )}
                    {positionsFetchedAt && (
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        as of {positionsFetchedAt.toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata',
                        })} IST
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Semantics */}
              <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <p className="font-medium text-gray-900 dark:text-white">What will happen:</p>
                {isPause ? (
                  <ul className="space-y-1.5 list-disc list-inside marker:text-gray-400">
                    <li>
                      Every strategy in this basket is flipped to <span className="font-medium">INACTIVE</span>.
                    </li>
                    <li>
                      <span className="font-medium">New entries stop immediately</span> — for you AND every subscriber.
                    </li>
                    <li>
                      Open positions continue to exit naturally per their schedule, SL, or TP. Exits keep firing for paused strategies.
                    </li>
                    <li>The listing stays published — subscribers can still see it and the marketplace browse still surfaces it.</li>
                    <li>You can resume anytime; nothing is destructive.</li>
                  </ul>
                ) : (
                  <ul className="space-y-1.5 list-disc list-inside marker:text-gray-400">
                    <li>
                      Every paused strategy in this basket is flipped back to <span className="font-medium">ACTIVE</span>.
                    </li>
                    <li>
                      <span className="font-medium">Entries resume firing</span> on the next scheduled tick — for you AND every subscriber.
                    </li>
                    <li>No retroactive entries — missed slots stay missed.</li>
                  </ul>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-800 dark:text-red-200">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                  className={`flex-1 ${confirmBtn}`}
                  leftIcon={isSubmitting ? undefined : <Icon className="h-4 w-4" />}
                >
                  {isSubmitting
                    ? (isPause ? 'Pausing…' : 'Resuming…')
                    : (isPause ? 'Pause strategies' : 'Resume strategies')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </FocusedDialog>
    </div>
  );
};

export default PauseStrategiesDialog;
